import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { withStyles } from 'theme/withStyles';
import RawHtml from 'common/RawHtml';
import Offer from 'containers/Offer';
import BottomContent from 'common/BottomContent';
import ErrorMsg from 'common/ErrorMsg';
import Bundle from 'offers/Bundle';
import ButtonRowGroup from './common/ButtonRowGroup';

import commonStyles from './styles';

const styles = (theme) => {
  const common = commonStyles(theme);

  return {
    ...common,
    contentLeft: {
      marginBottom: 60,
      marginTop: 15,
      textAlign: 'left',
    },
    contentWrap: {
      margin: '0 auto 40px auto',
      maxWidth: 450,
    },
    crossSellResource: {
      ...theme.typography.p,
      ...theme.typography.p1,
      '& *:last-child': {
        marginBottom: 0,
        paddingBottom: 0,
      },
      [`@media(min-width: ${theme.screenSizes.mobileMax}px)`]: {
        fontSize: theme.typography.p1.desktopFontSize,
      },
      margin: [0, 0, theme.spacing.sm, 0],
      textAlign: 'center',
    },
    fauxLabel: {
      ...theme.typography.h1,
      display: 'block',
      marginBottom: 20,
      textAlign: 'center',
    },
    fauxSubText: {
      textAlign: 'center',
    },
    leftContent: {
      textAlign: 'left',
    },
    primaryOffer: {
      marginTop: 36,
    },
  };
};

const OffersQualifiedBare = ({
  bottomContent,
  classes,
  crossSaleBundles,
  crossSaleOffers,
  data,
  hasFields,
  hasRealBundles,
  isDeclinedView,
  label,
  labelMobile,
  name,
  offers,
  primaryBundle,
  resources,
  selectAllBundles,
  subText,
}) => {
  const bundlesAdded = selectAllBundles.filter((b) =>
    b.offers.every((o) => data[offers[o].fieldChosen]),
  );
  const bundlesNotAdded = selectAllBundles.filter((b) =>
    b.offers.every((o) => !data[offers[o].fieldChosen]),
  );
  const offersAdded = crossSaleOffers.filter((key) => data[offers[key].fieldChosen]);
  const offersNotAdded = crossSaleOffers.filter((key) => !data[offers[key].fieldChosen]);

  return (
    <>
      {!isDeclinedView || (!resources.title && !resources.subTitle) ? (
        <>
          <legend
            className={classnames('grp-label', classes.label)}
            role={!hasFields ? 'presentation' : undefined}
          >
            <RawHtml type="span" className={classnames('desktop', classes.desktopLabel)}>
              {label}
            </RawHtml>
            <RawHtml type="span" className={classnames('mobile', classes.mobileLabel)}>
              {labelMobile}
            </RawHtml>
          </legend>

          <RawHtml className={classnames('content', classes.content)}>{subText}</RawHtml>
        </>
      ) : (
        <>
          <legend
            className={classnames('grp-label', classes.label)}
            role={!hasFields ? 'presentation' : undefined}
          >
            <RawHtml type="span" className={classnames('desktop', classes.desktopLabel)}>
              {resources.title}
            </RawHtml>
            <RawHtml type="span" className={classnames('mobile', classes.mobileLabel)}>
              {resources.title}
            </RawHtml>
          </legend>
          <div className={classnames('content-wrap', classes.contentWrap)}>
            <RawHtml className={classnames('content', classes.content, classes.contentLeft)}>
              {resources.subTitle}
            </RawHtml>

            <RawHtml type="span" className={classnames('faux-label', classes.fauxLabel)}>
              {label}
            </RawHtml>
            <RawHtml className={classnames('faux-sub-text', classes.fauxSubText)}>
              {subText}
            </RawHtml>
          </div>
        </>
      )}

      <input type="hidden" value="break-autocomplete" />

      <ErrorMsg name={name} type="group" />

      {primaryBundle?.offers?.length > 0 && (
        <>
          <div className={classnames('region-primary-offer', classes.primaryOffer)}>
            <div className="offers">
              {!hasRealBundles ? (
                <Bundle {...primaryBundle} isPrimary hasRealBundles={false} />
              ) : null}
            </div>
          </div>

          <div className="region-primary-bundle">
            <div className="offers">
              {hasRealBundles ? (
                <div className="primary-bundle">
                  <Bundle {...primaryBundle} isPrimary />
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}

      {hasRealBundles ? (
        <div className="region-cross-sale-bundles">
          <div className="offers">
            {crossSaleBundles.map((b) => (
              <Bundle {...b} key={b.name} />
            ))}
          </div>
        </div>
      ) : (
        <>
          <div className="region-cross-sale-added">
            <div className="offers">
              <>
                {bundlesAdded.map((b) => (
                  <Bundle {...b} key={b.name} />
                ))}
                {offersAdded.map((offer) => (
                  <Offer key={offer} offerName={offer} />
                ))}
              </>
            </div>
          </div>
          <RawHtml
            type={isDeclinedView ? 'div' : 'p'}
            className={classnames('center cross-sell-resource', classes.crossSellResource, {
              hidden: resources.displayCrossSellSubText === 'false' && offersNotAdded.length === 0,
            })}
          >
            {isDeclinedView ? resources.declineCrossSaleSubText : resources.crossSaleSubText}
          </RawHtml>
          <div className="region-cross-sale">
            <div className="offers">
              {bundlesNotAdded.map((b) => (
                <Bundle {...b} key={b.name} />
              ))}
              {offersNotAdded.map((offer) => (
                <Offer key={offer} offerName={offer} />
              ))}
            </div>
          </div>
        </>
      )}

      <BottomContent className="bottomContent">{bottomContent}</BottomContent>

      <ButtonRowGroup groupName={name} />
    </>
  );
};

OffersQualifiedBare.propTypes = {
  bottomContent: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  crossSaleBundles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  crossSaleOffers: PropTypes.arrayOf(PropTypes.string).isRequired,
  data: PropTypes.shape({}).isRequired,
  hasFields: PropTypes.bool,
  hasRealBundles: PropTypes.bool,
  isDeclinedView: PropTypes.bool,
  label: PropTypes.string,
  labelMobile: PropTypes.string,
  name: PropTypes.string.isRequired,
  offers: PropTypes.shape({}).isRequired,
  primaryBundle: PropTypes.shape({
    offers: PropTypes.arrayOf(PropTypes.string),
    type: PropTypes.string.isRequired,
  }).isRequired,
  resources: PropTypes.objectOf(PropTypes.string),
  selectAllBundles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  subText: PropTypes.string,
};

OffersQualifiedBare.defaultProps = {
  bottomContent: '',
  hasFields: false,
  hasRealBundles: false,
  isDeclinedView: false,
  label: '',
  labelMobile: '',
  resources: {},
  subText: '',
};

const mapStateToProps = ({ bundles, configs, data, groups, offers }, { name }) => {
  const group = groups[name];
  const bundleArr = Object.entries(bundles)
    .map(([, entry]) => entry)
    .sort((a, b) => (a.order < b.order ? -1 : 1));

  const hasRealBundles = configs.selfService;
  const primaryBundle = bundles.requestBundle;
  /**
   * used to grab the offers that are not part of select_all bundles when hasRealBundles is false
   * used to grab all bundles (except for the requestBundle) when hasRealBundles is true
   * */
  const crossSaleBundles = bundleArr.filter(
    (b) => b.name !== 'requestBundle' && b.offers && (b.type !== 'SELECT_ALL' || hasRealBundles),
  );
  /** only used when hasRealBundles is false */
  const selectAllBundles = bundleArr.filter(
    (b) => b.name !== 'requestBundle' && b.offers && b.type === 'SELECT_ALL',
  );
  const crossSaleOffers = hasRealBundles
    ? []
    : crossSaleBundles.reduce((arr, b) => [...arr, ...b.offers], []);

  return {
    bottomContent: group.bottomContent,
    crossSaleBundles,
    crossSaleOffers,
    data,
    hasFields: group?.fields?.length > 0 ?? false,
    hasRealBundles,
    label: group.label,
    labelMobile: group.labelMobile,
    offers,
    primaryBundle,
    resources: group.resources,
    selectAllBundles,
    subText: group.subText,
  };
};

const OffersQualifiedStyled = withStyles(styles)(OffersQualifiedBare);

export default connect(mapStateToProps)(OffersQualifiedStyled);
